import React, { useState, useEffect, useCallback } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line } from 'recharts';
import { authenticatedFetch } from '../utils/apiUtils';
import './StatsScreen.css';

interface Vocable {
  id: string;
  japanese: string;
  kana: string;
  english: string[];
  nextReview?: string; // ISO date string or timestamp
  createdAt?: number; // Timestamp in milliseconds
}

interface StatsScreenProps {
  onBack: () => void;
}

interface WeeklyReviewData {
  week: string;
  count: number;
}

interface DailyLearnedData {
  date: string;
  count: number;
}

// Process vocables into weekly review data
const processWeeklyReviewData = (vocables: Vocable[]): WeeklyReviewData[] => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Create a map for weekly buckets (12 weeks from today)
    // Keys are "1 week", "2 weeks", ..., "12 weeks"
    const weeklyMap = new Map<string, number>();
    
    // Initialize 12 weeks
    for (let i = 1; i <= 12; i++) {
      let weekLabel: string;
      if (i === 1) {
        weekLabel = '1 week';
      } else if (i === 12) {
        weekLabel = '≥12 weeks';
      } else {
        weekLabel = `${i} weeks`;
      }
      weeklyMap.set(weekLabel, 0);
    }
    
    // Count vocables by week based on nextReview date
    vocables.forEach((vocable) => {
      let weekIndex: number;
      
      if (!vocable.nextReview) {
        // Vocables without nextReview count as to be reviewed today (week 1)
        weekIndex = 1;
      } else {
        let reviewDate: Date | null = null;
        try {
          // Try parsing as ISO date string first
          reviewDate = new Date(vocable.nextReview);
          // If that fails or is invalid, try as timestamp
          if (isNaN(reviewDate.getTime())) {
            const timestamp = typeof vocable.nextReview === 'string' 
              ? parseInt(vocable.nextReview, 10) 
              : vocable.nextReview;
            reviewDate = new Date(timestamp);
          }
        } catch {
          // If parsing fails, treat as today (week 1)
          reviewDate = null;
        }
        
        if (!reviewDate || isNaN(reviewDate.getTime())) {
          // Invalid date, treat as today (week 1)
          weekIndex = 1;
        } else {
          reviewDate.setHours(0, 0, 0, 0);
          
          // Calculate weeks from today
          const diffTime = reviewDate.getTime() - today.getTime();
          const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
          const diffWeeks = Math.floor(diffDays / 7) + 1; // +1 because week 1 is "this week"
          
          // Clamp to 1-12 weeks
          if (diffWeeks < 1) {
            weekIndex = 1; // Past dates count as week 1
          } else if (diffWeeks > 12) {
            weekIndex = 12; // Future dates beyond 12 weeks count as week 12
          } else {
            weekIndex = diffWeeks;
          }
        }
      }
      
      let weekLabel: string;
      if (weekIndex === 1) {
        weekLabel = '1 week';
      } else if (weekIndex === 12) {
        weekLabel = '≥12 weeks';
      } else {
        weekLabel = `${weekIndex} weeks`;
      }
      const currentCount = weeklyMap.get(weekLabel) || 0;
      weeklyMap.set(weekLabel, currentCount + 1);
    });
    
    // Convert map to array and sort by week number
    const result: WeeklyReviewData[] = Array.from(weeklyMap.entries())
      .map(([week, count]) => ({ week, count }))
      .sort((a, b) => {
        // Extract week number for sorting
        // Handle "≥12 weeks" by treating it as 12
        const aNum = parseInt(a.week.replace('≥', ''), 10);
        const bNum = parseInt(b.week.replace('≥', ''), 10);
        return aNum - bNum;
      });
    
    return result;
  };

function StatsScreen({ onBack }: StatsScreenProps) {
  const [weeklyReviewData, setWeeklyReviewData] = useState<WeeklyReviewData[]>([]);
  const [dailyLearnedData, setDailyLearnedData] = useState<DailyLearnedData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  const loadVocables = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      // Load weekly review data
      const vocablesResponse = await authenticatedFetch('/api/vocabulary/vocables');

      if (!vocablesResponse.ok) {
        throw new Error('Failed to fetch vocabulary');
      }

      const vocablesData = await vocablesResponse.json();
      
      // Process vocables for weekly review chart
      const weeklyData = processWeeklyReviewData(vocablesData);
      setWeeklyReviewData(weeklyData);

      // Load daily learned data
      const dailyResponse = await authenticatedFetch('/api/training/daily-learned');

      if (!dailyResponse.ok) {
        throw new Error('Failed to fetch daily learned data');
      }

      const dailyData = await dailyResponse.json();
      setDailyLearnedData(dailyData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load statistics');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadVocables();
  }, [loadVocables]);


  if (isLoading) {
    return (
      <div className="stats-screen">
        <div className="stats-container">
          <div className="stats-header">
            <h1 className="stats-title">Statistics</h1>
            <button className="back-button" onClick={onBack}>
              ← Back
            </button>
          </div>
          <div className="stats-content">
            <p>Loading statistics...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="stats-screen">
        <div className="stats-container">
          <div className="stats-header">
            <h1 className="stats-title">Statistics</h1>
            <button className="back-button" onClick={onBack}>
              ← Back
            </button>
          </div>
          <div className="stats-content">
            <p className="error-message">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="stats-screen">
      <div className="stats-container">
        <div className="stats-header">
          <h1 className="stats-title">Statistics</h1>
          <button className="back-button" onClick={onBack}>
            ← Back
          </button>
        </div>

        <div className="stats-content">
          <div className="chart-section">
            <h2 className="chart-title">Scheduled Reviews (Next 12 Weeks)</h2>
            <p className="chart-description">Number of words scheduled for review by week</p>
            <div className="chart-container">
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={weeklyReviewData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="week" angle={-45} textAnchor="end" height={100} />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="count" fill="#4f46e5" name="Words to Review" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="chart-section">
            <h2 className="chart-title">Daily Learned Vocables</h2>
            <p className="chart-description">Number of vocables learned per day (Last 30 days)</p>
            <div className="chart-container">
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={dailyLearnedData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" angle={-45} textAnchor="end" height={100} />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="count" stroke="#4f46e5" strokeWidth={2} name="Vocables Learned" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default StatsScreen;

